import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import no_img from '../../../assets/images/picture.png';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { AppState } from '../../../context/stores/rootReducers';
import { actions } from '../../../context/stores/rootActions';
import { PreviewImage } from '../../../components/base/ImageBox';
import { ProductImageModel } from '../../../context/models/products-image/ProductImageModel';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import ProductImageDetail from './ProductImageDetail';
import { ConfirmModal } from '../../../components/common/ConfirmModal';

interface IProductImageTableProps {
	productId: number;
	title: string;
	description: string;
}

const ProductImageTable = ({ productId, title, description }: IProductImageTableProps) => {
	const dispatch = useDispatch();
	const { status, allProductImages, showConfirm, showDetail, selectedIds } = useSelector((state: AppState) => state.productImage);

	const [showPreview, setShowPreview] = useState({ show: true, image_path: '' });

	const dataSources = useMemo(() => {
		return allProductImages.sort((a, b) => a.sortOrder - b.sortOrder);
	}, [allProductImages]);

	const handleReloadDataWithFilter = useCallback(
		() => {
			dispatch(actions.productImage.getAllRequest(productId));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[productId]
	);

	const initProductImage = useMemo(() => {
		return {
			id: 0,
			productId: productId,
			path: '',
			title: title,
			description: description,
			sortOrder: 0,
		} as ProductImageModel;
	}, [productId, title, description]);

	const handleSaveData = (data: ProductImageModel) => {
		dispatch(actions.productImage.saveRequest(data));
	};

	const handleDeleteData = (ids: number[]) => {
		dispatch(actions.productImage.deleteRequest(ids));
	};

	const onChangeSelectedIds = (ids: number[]) => {
		dispatch(actions.productImage.changeSelectedIds(ids));
	};

	const onShowDetail = (data: BaseShowDetail<ProductImageModel>) => {
		dispatch(actions.productImage.showDetail(data));
	};

	const onShowConfirm = (isShow: boolean) => {
		dispatch(actions.productImage.showConfirm(isShow));
	};

	useEffect(() => {
		handleReloadDataWithFilter();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productId]);

	useEffect(() => {
		if (status == eBaseActionStatus.reload) handleReloadDataWithFilter();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<ProductImageModel>[] => {
		return [
			{
				accessorKey: 'path',
				header: 'Ảnh',
				size: 120,
				cell: ({ row }) => {
					const article = row.original;
					return (
						<div className='flex items-start gap-4 transition-colors py-1'>
							<div className='hidden md:flex flex-shrink-0 w-16 h-16 rounded-md border border-border/50 bg-background overflow-hidden items-center justify-center shadow-sm'>
								<img src={article.path?.length > 0 ? article.path : no_img} alt='ảnh đại diện' className='object-cover w-full h-full rounded-sm' />
							</div>
							<div className='flex-1 space-y-1.5'>
								<h6 className='text-sm font-semibold text-foreground/90'>
									<em className='ni ni-image text-primary mr-1 flex-shrink-0'></em>
									{article.title}
								</h6>
								<p className='text-xs text-muted-foreground hidden md:block italic truncate max-w-[500px]'>
									Mô tả: {article.description}
								</p>
							</div>
						</div>
					);
				}
			},
			{
				id: 'actions',
				header: 'Thao tác',
				meta: { align: 'center' },
				size: 80,
				cell: ({ row }) => (
					<div className="flex gap-2 justify-center">
						<ButtonLink
							onClick={() => onShowDetail({ isShow: true, detailData: row.original })}
							title='Sửa'
							icon='ni ni-edit'
							theme='link'
						/>
						<ButtonLink
							onClick={() => setShowPreview({ show: true, image_path: row.original.path })}
							title='Xem trước'
							icon='ni ni-eye'
							theme='link'
						/>
					</div>
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onShowDetail]);

	return (
		<>
			<div className='w-full'>
				{productId > 0 && (
					<div>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={dataSources}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination={false}
								enableGlobalFilter={false}
								toolbar={{
									isShowSearch: false,
									extraActions: (
										<div className="flex gap-2">
											<Button
												text='Tạo mới'
												icon='icon ni ni-plus'
												theme='primary'
												className='hidden md:inline-flex'
												onClick={() => onShowDetail({ isShow: true, detailData: initProductImage })}
											/>
											{selectedIds.length > 0 && (
												<Button
													text={`Xóa (${selectedIds.length})`}
													icon='icon ni ni-trash'
													className='hidden md:inline-flex'
													theme='danger'
													onClick={() => onShowConfirm(true)}
												/>
											)}
										</div>
									)
								}}
								onRefresh={handleReloadDataWithFilter}
							/>
						)}
					</div>
				)}
				{productId <= 0 && (
					<div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
						Vui lòng lưu sản phẩm để bổ sung ảnh sản phẩm.
					</div>
				)}
			</div>
			{showDetail && showDetail.isShow && (
				<>
					<ProductImageDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ảnh sản phẩm`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ảnh sản phẩm đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
			{showPreview && showPreview.show && (
				<PreviewImage image={showPreview.image_path} onClose={() => setShowPreview({ show: false, image_path: '' })} />
			)}
		</>
	);
};

export default ProductImageTable;
